import React, { type FC, useEffect, useMemo, useRef, useState } from "react";
import { UscLabel, useUscLabel } from "../../../../utils/labels";
import useHorizontalMenu from "../../../../hooks/useHorizontalMenu";
import * as Styles from "./HorizontalMenu.styles";
import Icon from "../../../../../shared-components/toyota/styles/atoms/Icon";
import { trackClickMenuSection } from "../../../../utils/tracking";
import { useUscSelector } from "../../../../redux/store";
import { scrollSelectorSectionMap } from "../../../../hooks/useMenuSectionSelectors";

const HorizontalMenu: FC = () => {
    const menuSliderWrapperRef = useRef<HTMLDivElement>(null);
    const menuSliderActiveItemRef = useRef<HTMLAnchorElement>(null);
    const menuContainerRef = useRef<HTMLDivElement>(null);
    const menuSliderRef = useRef<HTMLUListElement>(null);

    const [menuSticky, setMenuSticky] = useState(false);

    const {
        scrollSelectorsLabelMap,
        selectors,
        activeSelector,
        handleScroll,
        showLeftArrow,
        showRightArrow,
        scrollTo,
    } = useHorizontalMenu({
        menuRef: menuContainerRef,
        sliderRef: menuSliderRef,
        BREADCRUMBS_HEIGHT: Styles.BREADCRUMBS_HEIGHT,
        HORIZONTAL_MENU_HEIGHT: Styles.HORIZONTAL_MENU_HEIGHT,
        EXTRA_DETECTION_MARGIN: Styles.EXTRA_DETECTION_MARGIN,
        ARROW_WIDTH: Styles.ARROW_WIDTH,
        EXTRA_SCROLL_MARGIN: Styles.EXTRA_SCROLL_MARGIN,
    });

    const vehicleType = useUscSelector((state) => state.vehicle.type);
    const [nextLabel, previousLabel, ariaHorizontalMenuTitleLabel] = useUscLabel([
        "uscA11yButtonNext",
        "uscA11yButtonPrevious",
        "uscA11yMenuHorizontalTitle",
    ]);

    useEffect(() => {
        if (menuSliderWrapperRef?.current) {
            const sliderWrapper = menuSliderWrapperRef.current;
            const MENU_HEADER_HEIGHT = 80;
            const scrollListener = (): void => {
                const { y: sliderWrapperY } = sliderWrapper.getBoundingClientRect();
                if (sliderWrapperY > window.scrollY) {
                    // scrolling up
                    setMenuSticky(sliderWrapperY <= Styles.BREADCRUMBS_HEIGHT);
                } else {
                    // scrolling down
                    setMenuSticky(sliderWrapperY <= MENU_HEADER_HEIGHT + Styles.BREADCRUMBS_HEIGHT);
                }
            };

            window.addEventListener("scroll", scrollListener);
            return () => {
                window.removeEventListener("scroll", scrollListener);
            };
        }
    }, [menuSliderWrapperRef]);

    const horizontalMenuItems = useMemo(() => {
        return selectors.map((selector) => {
            const onClickHandler = (): void => {
                trackClickMenuSection(vehicleType, scrollSelectorSectionMap[selector]);
                scrollTo(selector);
            };

            return (
                <Styles.MenuListItem key={selector} aria-current={activeSelector === selector ? "location" : undefined}>
                    <Styles.MenuLink
                        ref={menuSliderActiveItemRef}
                        href={`#${selector}`}
                        onClick={(event) => {
                            event.preventDefault();
                            onClickHandler();
                        }}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                onClickHandler();
                                // Focus on the section after using "Enter"
                                setTimeout(() => {
                                    const element = document.getElementById(selector);
                                    (element as HTMLElement)?.focus();
                                }, 500);
                            }
                        }}
                        aria-current={activeSelector === selector ? "location" : undefined}
                    >
                        <UscLabel label={scrollSelectorsLabelMap[selector]} />
                    </Styles.MenuLink>
                </Styles.MenuListItem>
            );
        });
    }, [selectors, activeSelector, vehicleType]);

    // We show the menu when we have at least 2 results
    if (selectors.length < 2) return null;

    return (
        <Styles.Wrapper sticky={menuSticky} ref={menuSliderWrapperRef} aria-label={ariaHorizontalMenuTitleLabel}>
            <Styles.MenuListContainer ref={menuContainerRef}>
                <Styles.ButtonWrapper data-alignment="left" hidden={!showLeftArrow} aria-hidden={!showLeftArrow}>
                    <Styles.Button type="button" onClick={() => handleScroll("left")} aria-label={previousLabel}>
                        <Icon variant="chevron-left" />
                    </Styles.Button>
                </Styles.ButtonWrapper>
                <Styles.MenuList ref={menuSliderRef}>{horizontalMenuItems}</Styles.MenuList>
                <Styles.ButtonWrapper data-alignment="right" hidden={!showRightArrow} aria-hidden={!showRightArrow}>
                    <Styles.Button type="button" onClick={() => handleScroll("right")} aria-label={nextLabel}>
                        <Icon variant="chevron-right" />
                    </Styles.Button>
                </Styles.ButtonWrapper>
            </Styles.MenuListContainer>
        </Styles.Wrapper>
    );
};

export default HorizontalMenu;
